import React, { Suspense, useEffect, useMemo, useRef } from "react";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { ProceduralFigure } from "../props/NPCs";
import { COLORS } from "../constants";

// Tiny error-tolerant loader: if useGLTF throws (bad GLB, network, etc.) we
// fall back to the default procedural figure rather than crashing the scene.
function CustomGLTFAvatar({ url }) {
  const gltf = useGLTF(url, true);
  const ref = useRef();

  // Normalise scale + drop shadow casting (perf).
  const scene = useMemo(() => {
    const s = gltf.scene.clone(true);
    const box = new THREE.Box3().setFromObject(s);
    const size = new THREE.Vector3();
    box.getSize(size);
    const tallest = Math.max(size.x, size.y, size.z) || 1;
    // Target height ~1.7 units (matches our procedural figure).
    const targetH = 1.7;
    const k = targetH / Math.max(size.y, 0.001);
    s.scale.setScalar(k);
    // Re-centre so feet sit on y=0
    const box2 = new THREE.Box3().setFromObject(s);
    s.position.y -= box2.min.y;
    // Disable shadows
    s.traverse((o) => {
      if (o.isMesh) {
        o.castShadow = false;
        o.receiveShadow = false;
      }
    });
    return s;
  }, [gltf, gltf.scene]);

  return <primitive ref={ref} object={scene} />;
}

class GLTFErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(e) {
    // eslint-disable-next-line no-console
    console.warn("[Avatar] failed to render custom model, using default:", e);
  }
  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

/**
 * CharacterRig — wraps either the user's uploaded GLTF (.glb/.gltf) avatar
 * or a default procedural figure. Used by the third-person walker.
 */
export default function CharacterRig({ avatarUrl, isRenderableInScene }) {
  const fallback = (
    <ProceduralFigure
      robeColor={COLORS.navyDeep}
      sashColor={COLORS.gold}
      skinColor="#d8b48a"
    />
  );
  if (!avatarUrl || !isRenderableInScene) return fallback;
  return (
    <GLTFErrorBoundary fallback={fallback}>
      <Suspense fallback={fallback}>
        <CustomGLTFAvatar url={avatarUrl} />
      </Suspense>
    </GLTFErrorBoundary>
  );
}
